/**
 * What Hey Nova is doing, for the diagnostics a long press on "Hey Nova"
 * opens under the chat header: whether the microphone is listening, how close
 * the latest words came to waking it, how loud they were, and how the
 * microphone was opened. With it a tester can tell a phrase the model heard
 * but scored too low from one said while the microphone was closed or
 * reopening.
 *
 * Kept outside React: the engines note 12 chunks a second, which would
 * otherwise re-render the chat. The panel reads it while it is open.
 */

/** "The last few seconds": the best score and the loudest chunk are taken over this. */
const RECENT_MS = 10000;
/** The events the panel lists, newest first. */
const EVENTS_KEPT = 6;
/** The level of digital silence, and the bottom of the scale. */
const FLOOR_DB = -100;

export type WakeMicState =
  | 'off'
  | 'opening'
  | 'reopening'
  | 'warming up'
  | 'listening'
  | 'paused'
  | 'waiting for a tap'
  | 'failed';

export interface WakeWordEvent {
  at: number;
  text: string;
}

export interface WakeWordDiagnostics {
  mic: WakeMicState;
  /** The engine listening — openWakeWord or Picovoice — and whether it reports a score. */
  engine: string;
  scored: boolean;
  /** The latest chunk's score, and the best of the last ten seconds; 0 from an engine with no score. */
  score: number;
  bestScore: number;
  /** The latest chunk's level, and the loudest of the last ten seconds: dB below full scale. */
  level: number;
  loudest: number;
  wakes: number;
  /** Times scoring fell more than a second behind and started over. */
  fellBehind: number;
  /** Openings of the microphone that failed and were tried again. */
  reopenFailures: number;
  /** How the microphone was opened, where the platform says; '' where it does not. */
  audio: string;
  events: WakeWordEvent[];
}

let mic: WakeMicState = 'off';
let engine = 'openWakeWord';
let scored = true;
let audio = '';
let wakes = 0;
let fellBehind = 0;
let reopenFailures = 0;
let recent: Array<{at: number; score: number | null; level: number}> = [];
let events: WakeWordEvent[] = [];

const note = (text: string) => {
  events = [{at: Date.now(), text}, ...events].slice(0, EVENTS_KEPT);
};

const dropOld = (now: number) => {
  let oldest = recent[0];
  while (oldest && now - oldest.at > RECENT_MS) {
    recent.shift();
    oldest = recent[0];
  }
};

/** A rejection's code where the native side gives one ("MIC_BUSY"), else its message. */
export const describeError = (error: unknown): string => {
  const {code, message} = (error ?? {}) as {code?: unknown; message?: unknown};
  if (typeof code === 'string' && code) {
    return code;
  }
  return typeof message === 'string' && message ? message : String(error);
};

/** The loudness of 16-bit samples, in dB below full scale. */
export const levelOf = (samples: Int16Array): number => {
  if (!samples.length) {
    return FLOOR_DB;
  }
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const sample = (samples[i] ?? 0) / 32768;
    sum += sample * sample;
  }
  const rms = Math.sqrt(sum / samples.length);
  return rms > 0 ? Math.max(FLOOR_DB, 20 * Math.log10(rms)) : FLOOR_DB;
};

/**
 * What the iPhone's or iPad's AudioRecorderModule.startWakeMic says about
 * the microphone it opened, in a line. Anything else — Android says only
 * that it opened — is ''.
 */
export const describeNativeAudio = (details: unknown): string => {
  if (!details || typeof details !== 'object') {
    return '';
  }
  const {category, mode, voiceProcessing, sampleRate, channels, input} =
    details as {
      category?: string;
      mode?: string;
      voiceProcessing?: boolean;
      sampleRate?: number;
      channels?: number;
      input?: string;
    };
  const rate =
    typeof sampleRate === 'number' && sampleRate > 0
      ? `${Math.round(sampleRate / 100) / 10} kHz${
          channels && channels > 1 ? ` ×${channels}` : ''
        } → 16 kHz`
      : '';
  return [
    (category ?? '').replace('AVAudioSessionCategory', ''),
    (mode ?? '').replace('AVAudioSessionMode', ''),
    `voice processing ${voiceProcessing ? 'on' : 'off'}`,
    rate,
    input ?? '',
  ]
    .filter(Boolean)
    .join(' · ');
};

/** The microphone's state; `detail` says why, where there is more to say. */
export const noteMic = (state: WakeMicState, detail?: string): void => {
  if (state === mic && !detail) {
    return;
  }
  mic = state;
  note(detail ? `${state}: ${detail}` : state);
};

export const noteAudio = (description: string): void => {
  audio = description;
};

/** The engine that starts listening; `withScores` when it reports a score per chunk. */
export const noteEngine = (name: string, withScores: boolean): void => {
  if (name !== engine) {
    recent = [];
  }
  engine = name;
  scored = withScores;
};

/**
 * One chunk heard: its score, and the samples it came from. `null` from an
 * engine that reports no score (Porcupine), which still gets the level.
 */
export const noteChunk = (score: number | null, samples: Int16Array): void => {
  const now = Date.now();
  recent.push({at: now, score, level: levelOf(samples)});
  dropOld(now);
};

/** A wake, with the score that made it where the engine has one. */
export const noteWake = (score?: number): void => {
  wakes += 1;
  note(typeof score === 'number' ? `woke (${score.toFixed(2)})` : 'woke');
};

export const noteFellBehind = (): void => {
  fellBehind += 1;
  note('fell behind: started over');
};

export const noteReopenFailure = (attempt: number, error: unknown): void => {
  reopenFailures += 1;
  note(`opening ${attempt} failed: ${describeError(error)}`);
};

export const readWakeWordDiagnostics = (): WakeWordDiagnostics => {
  dropOld(Date.now());
  const latest = recent[recent.length - 1];
  return {
    mic,
    engine,
    scored,
    score: latest?.score ?? 0,
    bestScore: recent.reduce(
      (best, entry) => Math.max(best, entry.score ?? 0),
      0,
    ),
    level: latest?.level ?? FLOOR_DB,
    loudest: recent.reduce(
      (loudest, entry) => Math.max(loudest, entry.level),
      FLOOR_DB,
    ),
    wakes,
    fellBehind,
    reopenFailures,
    audio,
    events,
  };
};

/** Back to how a fresh start finds it. */
export const resetWakeWordDiagnostics = (): void => {
  mic = 'off';
  engine = 'openWakeWord';
  scored = true;
  audio = '';
  wakes = 0;
  fellBehind = 0;
  reopenFailures = 0;
  recent = [];
  events = [];
};
